import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAuth,
  setPersistence,
  signInWithEmailAndPassword,
  browserLocalPersistence,
  browserSessionPersistence,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
} from "firebase/auth";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");

  // Firebase Auth instance
  const auth = getAuth();
  // React Router navigation
  const navigate = useNavigate();

  // Handle Email + Password login
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); // Clear previous errors

    try {
      // Decide on persistence based on the "Remember Me" checkbox
      const persistence = rememberMe
        ? browserLocalPersistence
        : browserSessionPersistence;
      await setPersistence(auth, persistence);

      // Sign in user with email and password
      await signInWithEmailAndPassword(auth, email, password);

      // Redirect on successful login
      navigate("/");
    } catch (err) {
      setError(err.message || "Failed to login");
    }
  };

  // Handle Google Sign-in
  const handleGoogleSignIn = async () => {
    setError(""); // Clear previous errors
    try {
      // Same "Remember Me" logic for Google sign-in
      const persistence = rememberMe
        ? browserLocalPersistence
        : browserSessionPersistence;
      await setPersistence(auth, persistence);

      // Sign in with Google popup
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);

      // Redirect on successful sign-in
      navigate("/");
    } catch (err) {
      setError(err.message || "Failed to sign in with Google");
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 text-gray-800">
      <main className="flex-1 flex items-center justify-center  py-6 max-w-[100vw] mx-auto">
        <div className="w-full max-w-md bg-white rounded-md shadow p-6">
          <h1 className="text-2xl font-bold mb-4">Login</h1>

          {/* Display an error (if any) */}
          {error && <div className="text-red-500 mb-2">{error}</div>}

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email
              </label>
              <input
                type="text"
                id="email"
                placeholder="Enter your email"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                placeholder="Enter your password"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {/* "Remember Me" Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="rememberMe" className="text-sm text-gray-700">
                Remember Me
              </label>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              className="w-full bg-blue-600 text-white text-sm font-semibold py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Log in
            </button>
          </form>

          {/* OR sign in with Google */}
          <div className="mt-4">
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center bg-red-500 text-white text-sm font-semibold py-2 rounded-md hover:bg-red-600 transition-colors"
            >
              <svg
                className="w-5 h-5 mr-2"
                viewBox="0 0 48 48"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="#EA4335"
                  d="M24 9.5c3.94 0 7.17 1.62 9.42 4.23l6.95-6.95C36.02 3.23 30.46.5 24 .5 14.73.5 6.51 6.21 2.74 14.13l7.97 6.18C12.55 13.08 17.76 9.5 24 9.5z"
                />
                <path
                  fill="#4285F4"
                  d="M46.5 24c0-1.51-.13-2.98-.38-4.4H24v8.34h12.7c-.55 2.22-1.87 4.09-3.73 5.35l7.97 6.18C44.68 35.85 46.5 30.28 46.5 24z"
                />
                <path
                  fill="#FBBC05"
                  d="M10.7 29.27A14.48 14.48 0 0 1 9.5 24c0-1.9.37-3.72 1.2-5.27l-7.97-6.18A23.725 23.725 0 0 0 .5 24c0 3.97.95 7.72 2.63 11.05l7.57-5.78z"
                />
                <path
                  fill="#34A853"
                  d="M24 47.5c6.46 0 11.91-2.13 15.88-5.77l-7.97-6.18c-2.15 1.45-4.9 2.3-7.91 2.3-6.23 0-11.48-4.22-13.35-10.04l-7.85 6.06C6.63 40.89 14.48 47.5 24 47.5z"
                />
                <path fill="none" d="M0 0h48v48H0z" />
              </svg>
              Log in with Google
            </button>
          </div>

          {/* Extra links */}
          <div className="mt-4 text-sm text-center space-y-2">
            <button
              type="button"
              className="text-blue-600 hover:underline block mx-auto"
              onClick={async () => {
                setError("");
                if (!email) {
                  setError("Enter your email above, then click “Forgot your password?” again.");
                  return;
                }
                try {
                  await sendPasswordResetEmail(auth, email);
                  setError(`A password reset link was sent to ${email}.`);
                } catch (err) {
                  setError(err.message || "The reset email couldn't be sent.");
                }
              }}
            >
              Forgot your password?
            </button>
            <p>
              Don’t have an account?{" "}
              <a
                href="/signup"
                className="text-blue-600 font-medium hover:underline"
              >
                Sign up
              </a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
